<template>
  <Transition name="fade">
    <Dialog
      v-if="priceBreakdownModal"
      :title="`Order #${dialogOrderNumberDisplay} ${order_ref.order_type_detail ? order_ref.order_type_detail : ''} Price Breakdown`"
      :showCloseButton="true"
      :showFooter="can('update-price-breakdown')"
      :showSaveButton="false"
      :showCancelButton="false"
      :custom-footer="true"
      :lg="true"
      :loading="loading"
      @closeEvent="closeEvent"
    >
      <template #header-right>
        <div class="flex items-center gap-2 ml-auto mr-5">
          <div class="flex items-center gap-2">
            <template v-if="statusObjects.length > 0">
              <span class="rounded-full p-2" :class="statusObjects[0].color">
                <component
                  :is="statusObjects[0].icon"
                  class="w-6 h-6"
                  :stroke-width="3"
                />
              </span>
              <span class="flex flex-col mr-12">
                <a

                  v-if="stripeInvoiceItem?.stripe_invoice_status !== 'manual'"
                  :href="`${stripeDashboardUrl}/invoices/${statusObjects[0].status}`"

                  target="_blank"
                  class="capitalize mr-8 font-bold"
                >
                  {{ statusObjects[0].status ?? 'N/A' }}
                </a>
                <span v-else class="capitalize mr-8 font-bold">
                  {{ statusObjects[0].status ?? 'N/A' }}
                </span>
                <span class="font-light">{{ statusObjects[0].description }}</span>
              </span>
            </template>
           <template v-if="stripeInvoiceItem?.stripe_invoice_number">
              <span>Invoice No:</span>
              <span v-if="!editingInvoiceNo">
                {{ stripeInvoiceItem.stripe_invoice_number }}
                <button
                  class="ml-2 px-4 py-2 text-normal border border-[#219f84] rounded text-[#219f84] hover:bg-[#219f84] hover:text-white"
                  @click="() => startEditingInvoiceNo()"
                >
                  Edit Invoice Number
                </button>
              </span>
              <span v-else class="flex items-center gap-2">
                <input
                  v-model="invoiceNoInput"
                  type="text"
                  placeholder="Edit Invoice No"
                  class="px-2 py-2 border border-gray-300 rounded-md w-40"
                />
                <button
                  @click="saveInvoiceNumber"
                  :disabled="savingInvoice || !invoiceNoInput.trim()"
                  class="flex gap-x-2 px-3 py-2 items-center text-[#219f84] border border-[#219f84] rounded-md cursor-pointer hover:text-white hover:bg-[#219F84] duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-[#219f84] disabled:hover:bg-white relative"
                >

                  Save
                </button>
                <button
                  @click="editingInvoiceNo = false"
                  class="flex px-3 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-200"
                >
                  Cancel
                </button>
              </span>
            </template>
            <template v-else>
              <span>Invoice Status:</span>
              <span>Not invoiced</span>
              <span class="flex items-center gap-2">
                <input
                  v-model="invoiceNoInput"
                  type="text"
                  placeholder="Enter Invoice No"
                  class="px-2 py-2 border border-gray-300 rounded-md w-100"
                />
                <button
                  v-if="invoiceNoInput && invoiceNoInput.toString().trim().length > 0"
                  @click="saveInvoiceNumber"
                  :disabled="savingInvoice"
                  class="flex gap-x-2 px-3 py-2 items-center text-[#219f84] border border-[#219f84] rounded-md cursor-pointer hover:text-white hover:bg-[#219F84] duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-[#219f84] disabled:hover:bg-white relative"
                >
                  <span v-if="savingInvoice" class="inline-block w-4 h-4 border-2 border-white rounded-full border-r-transparent animate-spin mr-2"></span>
                  Save Invoice No
                </button>
              </span>
            </template>
          </div>
          <!-- if user has update-price-breakdown permission, then save the order item and create invoice, otherwise just create invoice -->
          <button
            v-if="can('create-stripe-invoice') && !stripeInvoiceItem?.stripe_invoice_id"
            class="flex gap-x-2 px-3 py-2 items-center text-[#219f84] border border-[#219f84] rounded-md cursor-pointer hover:text-white hover:bg-[#219F84] duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-[#219f84] disabled:hover:bg-white relative"
            @click="onCreateInvoiceClick"
            :disabled="stripeLoading"
          >
            <div
              v-if="stripeLoading"
              class="absolute inset-0 bg-white/50 flex items-center justify-center z-[100]"
            >
              <span
                class="border-[4px] border-primary rounded-full w-10 h-10 animate-spin border-r-transparent"
              ></span>
            </div>
            {{
              stripeInvoiceItem?.stripe_invoice_id
                ? 'Credit current Invoice and create new'
                : 'Create Invoice'
            }}
          </button>

          <Dropdown
            v-if="stripeInvoiceItem?.stripe_invoice_info?.invoice_pdf"
            triggerStyle="flex gap-x-2 px-3 py-2 items-center text-[#219f84] border border-[#219f84] rounded-md cursor-pointer hover:text-white hover:bg-[#219F84]"
            :close-on-outside-click="true"
            :close-on-select="true"
          >
            <template #trigger>
              <span class="flex gap-1 items-center justify-center"
                >Actions <ChevronDownIcon class="size-5"
              /></span>
            </template>
            <template #contents>
              <ul>
                <li>
                  <a
                    class="px-2 py-2 text-[#292D32] cursor-pointer duration-300 hover:bg-[#F4F4F4] flex items-center justify-between gap-2"
                    :href="stripeInvoiceItem?.stripe_invoice_info?.invoice_pdf"
                    target="_blank"
                  >
                    Download Invoice PDF
                  </a>
                </li>
              </ul>
            </template>
          </Dropdown>
        </div>
      </template>
      <template #modal-content>
        <template v-for="(orderItemRef, orderItemRefIndex) in order_items">
          <template
          v-for="(factory_product, factoryProductIndex) in orderItemRef.factory_products"
          :key="`order-item-${orderItemRefIndex}factory_product_${factoryProductIndex}`"
        >
          <div class="flex justify-between gap-10 mb-5">
            <div class="flex items-center gap-2 mt-2 h-max">
              <img
                :src="getFilePreview(factory_product)"
                alt="Sku preview"
                class="w-20 border-0 img-thumbnail img-fluid"
                v-img-fallback
              />
              <div class="flex flex-col">
                <input v-if="factory_product.is_custom_product"
                  :disabled="!can('update-price-breakdown')"
                  class="px-4 py-1 mr-2 border border-gray-300 rounded-md"
                  v-model="factory_product.new_product_name"
                />
                <input v-else
                  :disabled="!can('update-price-breakdown')"
                  class="px-4 py-1 mr-2 border border-gray-300 rounded-md"
                  v-model="factory_product.product_name"
                />
                <span class="text-black-50">
                  SKU: {{ factory_product.sku_number
                  }}<template v-if="factory_product.sku?.sku_id != null">
                    ({{ factory_product.sku.sku_id }})</template
                  >
                </span>
                <span class="text-black-50 text-sm mt-0.5">
                  Design Name:
                  {{ factoryProductDesignLabel(factory_product) }}
                </span>
                <button
                  v-if="!factory_product.id"
                  @click="removeFactoryProduct(orderItemRefIndex, factoryProductIndex)"
                  class="flex p-1 text-red-500 transition-all border border-red-500 rounded-md cursor-pointer gap-x-1 w-max hover:bg-red-500 hover:text-white"
                >
                  <trash-icon class="size-5" />
                  Delete
                </button>
              </div>
            </div>
            <div class="flex flex-1 gap-10 max-w-[70%]">
              <div class="flex-1">
                <table class="w-full products-table">
                  <thead v-if="factoryProductIndex === 0">
                    <tr class="text-center">
                      <th class="px-4 py-2 font-semibold text-left">Size</th>
                      <th class="px-4 py-2 font-semibold">Quantity</th>
                      <th class="px-4 py-2 font-semibold">Price</th>
                      <th class="px-4 py-2 font-semibold">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      class="text-center"
                      v-for="(size, size_idx) in factory_product.prices.sizes"
                      :key="size_idx"
                    >
                      <td class="p-1 w-[25%] text-left">{{ size.size }}</td>
                      <td class="p-1 w-[25%]">{{ size.quantity }}</td>
                      <td class="p-1 w-[25%]">
                        <span
                          ><input
                            :disabled="!can('update-price-breakdown')"
                            type="text"
                            inputmode="decimal"
                            class="w-20 p-1 mr-2 border border-gray-300 rounded-md outline-none"
                            :value="size.final_net_price"
                            @change="
                              (event: Event) => {
                                const input = event.target as HTMLInputElement;
                                const number = formatNumber(input.value)
                                input.value = number
                                size.final_net_price = number
                                handlePriceUpdate(size, size.quantity);
                              }
                            "
                          />{{
                            orderItemNetPriceCurrency?.code ??
                            ''
                          }}</span
                        >
                      </td>
                      <td class="p-1 w-[25%] text-right">
                        {{ formatNumber(size.final_net_price_with_quantity) }}
                        <strong>{{
                          orderItemNetPriceCurrency?.code ?? ''
                        }}</strong>
                      </td>
                    </tr>
                    <tr v-if="factory_product.prices.total_addons > 0">
                      <td colspan="4">
                        <h1
                          class="p-1 mb-1 font-semibold text-center border text-md border-grey-300"
                        >
                          Addons
                        </h1>
                        <table class="w-full mt-4">
                          <tbody>
                            <!-- simple addons starts -->
                            <template
                              v-for="(addon, addon_idx) in factory_product.prices.addons"
                              :key="addon_idx"
                            >
                              <tr class="text-center">
                                <td class="p-1 w-[25%] text-left">
                                  {{ addon.addon_name }}
                                </td>
                                <td class="p-1 w-[25%]"></td>
                                <td class="p-1 w-[25%]">
                                  <input
                                    :disabled="!can('update-price-breakdown')"
                                    type="text"
                                    inputmode="decimal"
                                    class="w-20 p-1 mr-2 border border-gray-300 rounded-md outline-none"
                                    v-model="addon.final_net_price"
                                    @change="
                                      () => {
                                        addon.final_net_price = addon.final_net_price
                                          ? formatNumber(addon.final_net_price)
                                          : 0;
                                        handlePriceUpdate(
                                          addon,
                                          factory_product.prices?.total_quantity
                                        );
                                      }
                                    "
                                  />
                                  {{
                                    orderItemNetPriceCurrency
                                      ?.code ?? ''
                                  }}
                                </td>
                                <td class="p-1 w-[25%] text-right">
                                  {{ formatNumber(addon.final_net_price_with_quantity) }}
                                  <strong>{{
                                    orderItemNetPriceCurrency
                                      ?.code ?? ''
                                  }}</strong>
                                </td>
                              </tr>
                            </template>
                            <!-- simple addons ends -->
                            <!-- ungrouped addons starts -->
                            <template
                              v-for="(ungrouped_addon, ungrouped_addon_idx) in factory_product
                                .prices.ungrouped_addons"
                              :key="`ungrouped_addon_${ungrouped_addon_idx}`"
                            >
                              <tr class="text-center">
                                <td class="p-1 w-[25%] text-left">
                                  {{ ungrouped_addon.addon_name }}
                                </td>
                                <td class="p-1 w-[25%]"></td>
                                <td class="p-1 w-[25%]">
                                  <input
                                    :disabled="!can('update-price-breakdown')"
                                    type="text"
                                    inputmode="decimal"
                                    class="w-20 p-1 mr-2 border border-gray-300 rounded-md outline-none"
                                    v-model="ungrouped_addon.final_net_price"
                                    @change="
                                      () => {
                                        ungrouped_addon.final_net_price =
                                          ungrouped_addon.final_net_price
                                            ? parseFloat(ungrouped_addon.final_net_price)
                                            : 0;
                                        handlePriceUpdate(
                                          ungrouped_addon,
                                          factory_product.prices?.total_quantity
                                        );
                                      }
                                    "
                                  />
                                  {{
                                    orderItemNetPriceCurrency
                                      ?.code ?? ''
                                  }}
                                </td>
                                <td class="p-1 w-[25%] text-right">
                                  {{ formatNumber(ungrouped_addon.final_net_price_with_quantity) }}
                                  <strong>{{
                                    orderItemNetPriceCurrency
                                      ?.code ?? ''
                                  }}</strong>
                                </td>
                              </tr>
                            </template>
                            <!-- ungrouped addons ends -->
                            <!-- grouped addons starts -->
                            <template
                              v-for="(grouped_addon, group_name) in factory_product.prices
                                .grouped_addons"
                              :key="`grouped_${group_name}_selected_addon`"
                            >
                              <tr class="text-center">
                                <td class="p-1 w-[25%] text-left">
                                  {{ group_name }}:
                                  {{ grouped_addon.addon_name }}
                                </td>
                                <td class="p-1 w-[25%]"></td>
                                <td class="p-1 w-[25%]">
                                  <input
                                    :disabled="!can('update-price-breakdown')"
                                    type="text"
                                    inputmode="decimal"
                                    class="w-20 p-1 mr-2 border border-gray-300 rounded-md outline-none"
                                    v-model="grouped_addon.final_net_price"
                                    @change="
                                      () => {
                                        grouped_addon.final_net_price =
                                          grouped_addon.final_net_price
                                            ? parseFloat(grouped_addon.final_net_price)
                                            : 0;
                                        handlePriceUpdate(
                                          grouped_addon,
                                          factory_product.prices?.total_quantity
                                        );
                                      }
                                    "
                                  />{{
                                    orderItemNetPriceCurrency
                                      ?.code ?? ''
                                  }}
                                </td>
                                <td class="p-1 w-[25%] text-right">
                                  {{ formatNumber(grouped_addon.final_net_price_with_quantity) }}
                                  <strong>{{
                                    orderItemNetPriceCurrency
                                      ?.code ?? ''
                                  }}</strong>
                                </td>
                              </tr>
                            </template>
                            <!-- grouped addons ends -->
                            <!-- logo technology addons starts -->
                            <template
                              v-for="(logo_technology, logo_technologies_index) in factory_product
                                .prices.logo_technologies"
                              :key="`logo_technology_${logo_technologies_index}_addon`"
                            >
                              <tr class="text-center">
                                <td class="p-1 w-[25%] text-left">
                                  {{ logo_technology.addon_name }}
                                </td>
                                <td class="p-1 w-[25%]"></td>
                                <td class="p-1 w-[25%]">
                                  <input
                                    :disabled="!can('update-price-breakdown')"
                                    type="text"
                                    inputmode="decimal"
                                    class="w-20 p-1 mr-2 border border-gray-300 rounded-md outline-none"
                                    v-model="logo_technology.final_net_price"
                                    @change="
                                      () => {
                                        logo_technology.final_net_price =
                                          logo_technology.final_net_price
                                            ? parseFloat(logo_technology.final_net_price)
                                            : 0;
                                        handlePriceUpdate(
                                          logo_technology,
                                          factory_product.prices?.total_quantity
                                        );
                                      }
                                    "
                                  />{{
                                    orderItemNetPriceCurrency
                                      ?.code ?? ''
                                  }}
                                </td>
                                <td class="p-1 w-[25%] text-right">
                                  {{ formatNumber(logo_technology.final_net_price_with_quantity) }}
                                  <strong>{{
                                    orderItemNetPriceCurrency
                                      ?.code ?? ''
                                  }}</strong>
                                </td>
                              </tr>
                            </template>
                            <!-- logo technology addons ends -->
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <hr class="mt-1 border border-gray-300" />
                <table class="w-full">
                  <tbody>
                    <tr class="text-center">
                      <td class="p-1 w-[25%]"></td>
                      <td class="p-1 w-[25%]">
                        Qty:
                        <strong>{{ factory_product.prices?.total_quantity }}</strong>
                      </td>
                      <td class="p-1 w-[25%]"></td>
                      <td class="p-1 w-[25%] text-right">
                        <strong>Total:</strong>
                        {{ formatNumber(factory_product.prices.factory_product_total_price) }}
                        <strong>{{
                          orderItemNetPriceCurrency?.code ?? ''
                        }}</strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </template>
        </template>

        <!-- Custom order lines: same row layout as factory products; add via modal. -->
        <template v-for="(orderLineRow, orderLineIndex) in standaloneOrderLines" :key="'order-line-' + orderLineIndex">
          <div class="flex justify-between gap-10 mb-5">
            <div class="flex items-center gap-2 mt-2 h-max">
              <img
                :src="`${storage_url}/placeholders/custom-design.png`"
                alt="Order line"
                class="w-20 border-0 img-thumbnail img-fluid opacity-90"
                v-img-fallback
              />
              <div class="flex flex-col">
                <input
                  :disabled="!can('update-price-breakdown')"
                  class="px-4 py-1 mr-2 border border-gray-300 rounded-md max-w-[18rem]"
                  v-model="orderLineRow.name"
                  type="text"
                  placeholder="Line name"
                  @change="calculateTotalPrice"
                />
                <span class="text-xs text-gray-500 mt-0.5"
                  >Order line {{ orderLineIndex + 1 }} of {{ standaloneOrderLines.length }}</span
                >
                <div
                  v-if="can('update-price-breakdown')"
                  class="flex flex-wrap items-center gap-1.5 mt-1.5"
                >
                  <span class="text-[11px] font-medium text-gray-500 shrink-0">Pricing</span>
                  <div
                    class="inline-flex rounded-md border border-gray-200 bg-gray-100/90 p-0.5 shadow-inner"
                    role="group"
                    aria-label="Fixed or percent pricing"
                  >
                    <button
                      type="button"
                      class="min-w-[3.25rem] rounded px-1.5 py-1 text-[10px] font-semibold leading-tight transition-all"
                      :class="
                        orderLineUsesFixedPricing(orderLineRow)
                          ? 'bg-white text-[#1a7a66] shadow-sm ring-1 ring-gray-200/90'
                          : 'text-gray-500 hover:text-gray-800'
                      "
                      @click="setOrderLinePricingMode(orderLineIndex, 'fixed')"
                    >
                      Fixed
                    </button>
                    <button
                      type="button"
                      class="min-w-[3.75rem] rounded px-1.5 py-1 text-[10px] font-semibold leading-tight transition-all"
                      :class="
                        !orderLineUsesFixedPricing(orderLineRow)
                          ? 'bg-white text-amber-900 shadow-sm ring-1 ring-gray-200/90'
                          : 'text-gray-500 hover:text-gray-800'
                      "
                      @click="setOrderLinePricingMode(orderLineIndex, 'percent')"
                    >
                      Percent
                    </button>
                  </div>
                </div>
                <button
                  v-if="can('update-price-breakdown')"
                  type="button"
                  @click="removeOrderLineAt(orderLineIndex)"
                  class="flex p-1 mt-1 text-red-500 transition-all border border-red-500 rounded-md cursor-pointer gap-x-1 w-max hover:bg-red-500 hover:text-white"
                >
                  <trash-icon class="size-5" />
                  Delete
                </button>
              </div>
            </div>
            <div class="flex flex-1 gap-10 max-w-[70%]">
              <div class="flex-1">
                <table class="w-full products-table">
                  <tbody>
                    <tr class="text-center">
                      <td class="p-1 w-[25%] text-left"></td>
                      <td class="p-1 w-[25%]">
                        <input
                          :disabled="!can('update-price-breakdown')"
                          type="number"
                          min="1"
                          title="Quantity"
                          class="w-16 p-1 border border-gray-300 rounded-md outline-none text-center"
                          v-model.number="orderLineRow.quantity"
                          @change="onOrderLineQuantityChange(orderLineIndex)"
                        />
                      </td>
                      <td class="p-1 w-[25%]">
                        <template v-if="orderLineUsesFixedPricing(orderLineRow)">
                          <span
                            ><input
                              :disabled="!can('update-price-breakdown')"
                              type="text"
                              inputmode="decimal"
                              class="w-20 p-1 mr-2 border border-gray-300 rounded-md outline-none"
                              :value="formatOrderLineNumber(orderLineRow.price)"
                              @change="onStandaloneAddOrderLinePriceChange($event, orderLineIndex)"
                            />{{ orderItemNetPriceCurrency?.code ?? '' }}</span
                          >
                        </template>
                        <template v-else>
                          <div class="flex flex-col items-center gap-0.5">
                            <span class="inline-flex items-center gap-1 justify-center">
                              <input
                                :disabled="!can('update-price-breakdown')"
                                type="text"
                                inputmode="decimal"
                                title="Percentage"
                                class="w-16 p-1 border border-gray-300 rounded-md outline-none"
                                :value="formatOrderLineNumber(orderLineRow.percentage ?? 0)"
                                @change="onStandaloneAddOrderLinePercentageChange($event, orderLineIndex)"
                              />
                              <span class="text-sm">%</span>
                            </span>
                           
                          </div>
                        </template>
                      </td>
                      <td class="p-1 w-[25%] text-right">
                        {{ formatOrderLineNumber(standaloneOrderLineAmountAt(orderLineIndex)) }}
                        <strong>{{ orderItemNetPriceCurrency?.code ?? '' }}</strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <hr class="mt-1 border border-gray-300" />
                <table class="w-full">
                  <tbody>
                    <tr class="text-center">
                      <td class="p-1 w-[25%]"></td>
                      <td class="p-1 w-[25%]">
                        Qty:
                        <strong>{{ orderLineRow.quantity }}</strong>
                      </td>
                      <td class="p-1 w-[25%]"></td>
                      <td class="p-1 w-[25%] text-right">
                        <strong>Total:</strong>
                        {{ formatOrderLineNumber(standaloneOrderLineAmountAt(orderLineIndex)) }}
                        <strong>{{ orderItemNetPriceCurrency?.code ?? '' }}</strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </template>

        <hr class="border-gray-200" />
        <table class="w-full">
          <tbody>
            <tr>
              <td class="p-1 w-[25%]"></td>
              <td class="p-1 w-[25%]"></td>
              <td class="p-1 w-[25%]" style="text-align: left; padding-left: 4.5%">
                Total Qty:
                <strong>{{ order_items?.[0]?.price_info?.total_quantity }}</strong>
              </td>
              <td class="p-1 w-[25%] text-right">
                <strong>Total Price:</strong>
                {{ formatNumber(order_items?.[0]?.price_info?.total_price ?? 0) }}
                <strong>{{
                  orderItemNetPriceCurrency?.code ?? ''
                }}</strong>
              </td>
            </tr>
            <tr class="text-right">
              <td class="p-1 w-[25%]"></td>
              <td class="p-1 w-[25%]"></td>
              <td class="p-1 w-[25%]"></td>
              <td class="p-1 w-[25%]">
                <strong>Shipping Cost:</strong>
                {{ formatNumber(order_items?.[0]?.price_info?.shipping) }}
                <strong>{{
                  orderItemNetPriceCurrency?.code ?? ''
                }}</strong>
              </td>
            </tr>
            <tr class="text-right">
              <td class="p-1 w-[25%]"></td>
              <td class="p-1 w-[25%]"></td>
              <td class="p-1 w-[25%]"></td>

              <td class="p-1 w-[25%]" v-if="isFixedForUI">
                <strong>Discount:</strong>
                {{
                  formatNumber(
                    order_items?.[0]?.price_info?.fixed_value
                      ? order_items?.[0]?.price_info?.fixed_value
                      : 0
                  )
                }}
                <strong>{{
                  orderItemNetPriceCurrency?.code ?? ''
                }}</strong>
              </td>
              <td class="p-1 w-[25%]" v-else>
                <strong>Discount:</strong> {{ formatNumber(discount_amount) }}
                <strong>{{
                  orderItemNetPriceCurrency?.code ?? ''
                }}</strong>
              </td>
            </tr>
            <tr class="text-right">
              <td class="p-1 w-[25%]"></td>
              <td class="p-1 w-[25%]"></td>
              <td class="p-1 w-[25%]"></td>
              <td class="p-1 w-[25%]">
                <strong>Final Price:</strong>
                {{
                  formatNumber(finalPriceForUI)
                }}
                <strong>{{
                  orderItemNetPriceCurrency?.code ?? ''
                }}</strong>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="flex gap-x-2 mt-5" v-if="can('update-price-breakdown')">
          <button
            class="flex gap-x-2 px-3 py-2 items-center text-[#219f84] border border-[#219f84] rounded-md cursor-pointer hover:text-white hover:bg-[#219F84] duration-200"
            @click="addProductModal = true"
          >
            <plus-icon class="size-5"></plus-icon> Add Item
          </button>
          <button
            type="button"
            class="flex gap-x-2 px-3 py-2 items-center text-[#219f84] border border-[#219f84] rounded-md cursor-pointer hover:text-white hover:bg-[#219F84] duration-200"
            @click="openAddOrderLineModal"
          >
            <plus-icon class="size-5"></plus-icon>
            Add Order Line
          </button>
          <template v-if="can('update-techpack')">
            <a
              :href="`${appUrl}/edit-techpack/${order_ref.id}?source=new_dashboard`"
              class="border p-2 rounded-md border-[#219F84] text-[#219F84]"
            >
              Edit Tech Pack
            </a>
          </template>
        </div>
      </template>
      <template #footer>
      <div class="w-full flex flex-col">
       <div
          class="flex w-full items-end"
          :class="{
            'justify-between': can('update-price-breakdown'),
            'justify-end': !can('update-price-breakdown'),
          }"
        >
          <div class="flex gap-x-5 items-end" v-if="can('update-price-breakdown')">
            <button
              @click="shippingAndDiscountModal = true"
              class="border p-2 rounded-md border-[#219F84] text-[#219F84] h-max"
            >
              Shipping and discount
            </button>

            <div class="flex flex-col items-start gap-1">
              <label for="orderType" class="text-[14px]">Order Type:</label>
              <Select
                append-to-body
                v-model="selectedOrderType"
                :options="orderTypes"
                label="label"
                placeholder="Select Order Type"
                :clearable="false"
                @option:selected="handleOrderTypeUpdate"
                :class="{ 'border-red-500': errorMessages['order_type'] }"
                class="min-w-[300px] w-fit"
              />
            </div>
            <div class="flex flex-col items-start gap-1">
              <label for="orderType" class="text-[14px]">Invoice Memo:</label>
              <input
              class="w-full px-3 py-2 border border-gray-300 rounded-md outline-none"
              type="text"
              v-model="invoice_memo"
              placeholder="Invoice Memo"
             :disabled="!!stripeInvoiceItem?.stripe_invoice_id"
            />
            </div>
            <div class="flex flex-col items-start gap-1"  v-if="can('change-order-billing-merchant')">
              <label for="orderType" class="text-[14px]">Billing Merchant:</label>
               <Select
                  ref="billingSelect"
                  v-model="selectedBillingCompany"
                  :options="billingMerchant"
                  @update:modelValue="handleBillingMerchantUpdate"
                  label="company_name"
                  :reduce="merchant => merchant.id"
                  :clearable="false"
                  class="min-w-[300px] w-fit"
                  :disabled="!!stripeInvoiceItem?.stripe_invoice_id"
                />
            </div>
          </div>


          <div class="flex gap-x-5">
            <button
              class="border p-2 rounded-md bg-[#219F84] text-white h-max"
              @click="updateOrderPrices(false)"
            >
              Save
            </button>
          </div>
        </div>
      <div class="w-full mt-4  max-w-[50%]">
        <textarea
          v-model="internal_note"
          rows="2"
          class="w-full px-3 py-2 border border-gray-300 rounded-md outline-none"
          placeholder="Internal Note"
        ></textarea>
      </div>
      </div>

      </template>
    </Dialog>
  </Transition>
  <Transition name="fade">
    <Dialog
      v-if="addOrderLineModal"
      title="Add Order Line"
      :showCloseButton="true"
      :showFooter="true"
      :showSaveButton="false"
      :showCancelButton="false"
      :custom-footer="true"
      :sm="true"
      @closeEvent="closeAddOrderLineModal"
    >
      <template #modal-content>
        <div class="flex flex-col gap-4">
          <div>
            <label class="block mb-1 text-sm font-medium text-gray-700">Name</label>
            <input
              v-model="addOrderLineForm.name"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-md outline-none"
              placeholder="Line item name"
            />
          </div>
          <div>
            <label class="block mb-1 text-sm font-medium text-gray-700">Quantity</label>
            <input
              v-model.number="addOrderLineForm.quantity"
              type="number"
              min="1"
              class="w-full px-3 py-2 border border-gray-300 rounded-md outline-none"
            />
          </div>
          <div class="flex justify-between items-center gap-3">
            <label class="text-sm font-medium text-gray-700 shrink-0">{{
              addOrderLineForm.is_fixed ? 'Unit price' : 'Percentage'
            }}</label>
            <div class="flex items-center gap-x-2">
              <input
                id="add-order-line-fixed-only"
                v-model="addOrderLineForm.is_fixed"
                type="checkbox"
                name="Fixed unit price"
                class="w-4 h-4"
              />
              <label for="add-order-line-fixed-only" class="text-sm text-gray-700 whitespace-nowrap"
                >Fixed price</label
              >
            </div>
          </div>
          <template v-if="addOrderLineForm.is_fixed">
            <input
              v-model="addOrderLineForm.price"
              type="text"
              inputmode="decimal"
              class="w-full px-3 py-2 border border-gray-300 rounded-md outline-none"
              placeholder="0"
            />
          </template>
          <template v-else>
            <div class="relative">
              <input
                v-model="addOrderLineForm.percentage"
                type="text"
                inputmode="decimal"
                class="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md outline-none"
                placeholder="0"
              />
              <span class="absolute text-gray-500 transform -translate-y-1/2 right-3 top-1/2">%</span>
            </div>
          </template>
        </div>
      </template>
      <template #footer>
        <div class="flex justify-end w-full gap-2">
          <button
            type="button"
            class="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
            @click="closeAddOrderLineModal"
          >
            Cancel
          </button>
          <button
            type="button"
            class="px-4 py-2 rounded-md border border-[#219F84] text-[#219f84] hover:text-white hover:bg-[#219F84]"
            @click="saveAddOrderLine"
          >
            Add line
          </button>
        </div>
      </template>
    </Dialog>
  </Transition>
  <Transition name="fade">
    <AddProductModal
      v-if="addProductModal"
      @on-close="() => (addProductModal = false)"
      :customerId="(order_item as DashboardItem).order.customer_id"
      :companyId="(order_item as DashboardItem).order.company_id"
      :orderItem="order_item"
      :lockedFactoryId="addModalLockedFactoryId"
      :lockerRoom="lockerRoom"
      :factories="factories"
      :company_products="company_products"
      :copiedRoster="copiedRoster"
      @copy-roster="copyRoster"
      @add-factory-product="addFactoryProduct"
    />
  </Transition>
  <Transition name="fade">
    <Dialog
      v-if="shippingAndDiscountModal"
      title="Add Shipping and Discount"
      :showCloseButton="true"
      :showFooter="true"
      @close-event="discount_shipping_info = { ...discount_shipping_info, ...JSON.parse(JSON.stringify(order_items?.[0]?.price_info ?? order_ref.price_info)) }"
      @cancel-event="discount_shipping_info = { ...discount_shipping_info, ...JSON.parse(JSON.stringify(order_items?.[0]?.price_info ?? order_ref.price_info)) }"
      :showSaveButton="false"
      :showCancelButton="false"
      :custom-footer="true"
      :sm="true"
      @closeEvent="closeShippingAndDiscountModal"
    >
      <template #modal-content>
        <div>
          <div class="flex justify-between">
            <label class="block mb-1 text-sm font-medium text-gray-700">Add Discount</label>
            <div class="flex items-center gap-x-2">
              <input
                type="checkbox"
                name="Fixed Discount"
                class="w-4 h-4"
                tr
                v-model="discount_shipping_info.is_fixed"
              />
              <label for="Fixed Discount">Fixed Price</label>
            </div>
          </div>
          <template v-if="discount_shipping_info.is_fixed">
            <input
              class="w-full px-3 py-2 border border-gray-300 rounded-md outline-none"
              type="number"
              v-model="discount_shipping_info.fixed_value"
              @change="
                () => {
                  discount_shipping_info.fixed_value = discount_shipping_info.fixed_value
                    ? formatNumber(discount_shipping_info.fixed_value)
                    : 0;
                }
              "
              placeholder="Enter Fixed Price"
            />
          </template>
          <template v-else>
            <div class="relative">
              <input
                class="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md outline-none"
                type="number"
                v-model="discount_shipping_info.discount"
                @change="
                  () => {
                    discount_shipping_info.discount = discount_shipping_info.discount
                      ? formatNumber(discount_shipping_info.discount)
                      : 0;
                  }
                "
                placeholder="Enter Discount Percentage"
              />
              <span class="absolute transform -translate-y-1/2 right-3 top-1/2">%</span>
            </div>
          </template>
          <label class="block mt-4 mb-1 text-sm font-medium text-gray-700">Add Shipping</label>
          <input
            class="w-full px-3 py-2 border border-gray-300 rounded-md outline-none"
            type="number"
            v-model="discount_shipping_info.shipping"
            @change="
              () => {
                discount_shipping_info.shipping = discount_shipping_info.shipping
                  ? formatNumber(discount_shipping_info.shipping)
                  : 0;
              }
            "
            placeholder="Enter Shipping Cost"
          />
          <span class="absolute transform -translate-y-1/2 right-6" style="top: 65%">{{
            orderItemNetPriceCurrency?.code ?? ''
          }}</span>
        </div>
      </template>
      <template #footer>
        <div class="flex justify-end w-full">
          <button
            @click="calculateShippingAndDiscount"
            class="border px-4 py-2 rounded-md border-[#219F84] text-[#219F84]"
          >
            Save
          </button>
        </div>
      </template>
    </Dialog>
  </Transition>
</template>
<script setup lang="ts">
import Dialog from '@/components/Shared/Dialog.vue';
import Dropdown from '@/components/Shared/Dropdown.vue';
import Select from '@/components/Shared/Select.vue';
import { can } from '@/composables';
import { showConfirm, showToast } from '@/composables/swal';
import { handleResponseException, http } from '@/interceptors/axiosRequestInterceptor';
import { ApiResponse2, DashboardItem, OrderType, Product, Result } from '@/models';
import { normalizeOrderLinesFromApi, orderLineUsesFixedPricing } from '@/models/OrderItemOrderLine';
import { useMainStore } from '@/store';
import { orderTypes } from '@/utils/constants/orderTypes';
import { PlusIcon, TrashIcon } from '@heroicons/vue/24/outline';
import { AxiosError } from 'axios';
import { chunk } from 'lodash';
import { BadgeCheck, ChevronDownIcon, CircleX, Clock3, FileText, Trash2, TriangleAlert, SquareM } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { computed, onMounted, reactive, ref,watch } from 'vue';
import AddProductModal from './AddProductModal.vue';
import { CopiedRoster } from '@/models/CopiedRoster';
import Swal from "sweetalert2";
const { loading } = storeToRefs(useMainStore());
const stripeLoading = ref(false);

const stripeDashboardUrl = import.meta.env.VITE_APP_STRIPE_DASHBOARD_URL;

const props = defineProps<{
  order_item: any;
  order: any;
  appUrl: string;
}>();

const copiedRoster = ref<CopiedRoster>({
  isAnyRosterCopied: false,
  copiedFromProductIndex: null,
  rosterData: []
});

const order_ref = ref(JSON.parse(JSON.stringify(props.order)));
/** Mutable alias of `order_ref.value.items` — reassigned after opening refetch so mutations stay on the live tree. */
let order_items: any[] = order_ref.value.items ?? [];

/** Dialog title: first line `item_no`, else `order_no`, else N/A. */
const dialogOrderNumberDisplay = computed(() => {
  const itemNo = (order_ref.value.items?.[0] as Record<string, any> | undefined)?.item_no;
  if (itemNo != null && String(itemNo).trim() !== '') {
    return String(itemNo).trim();
  }
  const orderNo = order_ref.value.order_no;
  if (orderNo != null && String(orderNo).trim() !== '') {
    return String(orderNo).trim();
  }
  return 'N/A';
});

/** Stripe invoice fields live on the first line item, not on the parent order. */
const stripeInvoiceItem = computed(
  () => (order_items?.[0] ?? null) as Record<string, any> | null,
);

/** When the first line has a factory, Add Product locks the factory picker to that id. */
const addModalLockedFactoryId = computed((): number | string | null => {
  const id = (order_ref.value.items?.[0] as any)?.factory_id;
  if (id === null || id === undefined) return null;
  if (typeof id === 'string' && id.trim() === '') return null;
  return id;
});

/** Price labels use item `currency.net_price` (see order item currency column); then fallbacks. */
const orderItemNetPriceCurrency = computed((): Record<string, any> => {
  const item = order_items?.[0] as Record<string, any> | undefined;
  const pick = (obj: unknown): Record<string, any> | null =>
    obj && typeof obj === 'object' && Object.keys(obj as object).length
      ? (obj as Record<string, any>)
      : null;

  let v = pick(item?.currency?.net_price);
  if (v) return v;
  v = pick(item?.net_price);
  if (v) return v;
  v = pick(item?.additional_fields?.currency?.net_price);
  if (v) return v;
  return {};
});

/** Empty/missing: undefined, null, or blank string (0 and false are valid). */
const isMissingOrEmptyScalar = (v: unknown): boolean =>
  v === undefined || v === null || (typeof v === 'string' && v.trim() === '');

/** Prefer first order_item value, then order, then optional UI fallback. */
const pickOrderItemFirstScalar = <T>(
  itemVal: T | undefined | null,
  orderVal: T | undefined | null,
  uiFallback?: T,
): T | undefined => {
  if (!isMissingOrEmptyScalar(itemVal)) return itemVal as T;
  if (!isMissingOrEmptyScalar(orderVal)) return orderVal as T;
  return uiFallback;
};

const firstOrderItemSnapshot: any = order_items?.[0];
const orderBillingFallback =
  order_ref.value.billing_company_id || order_ref.value.company_id;
const propsBillingFallback =
  props.order?.billing_company_id || props.order?.company_id;

function factoryProductDesignLabel(factory_product: any): string {
  const isManual = order_ref.value.additional_fields?.is_manual_order === true;
  const designId =
    factory_product?.design_id != null && String(factory_product.design_id).trim() !== ''
      ? String(factory_product.design_id)
      : '';
  if (isManual) {
    const nick = factory_product?.design_nick_name;
    if (nick != null && String(nick).trim() !== '') {
      return String(nick).trim();
    }
    return designId;
  }
  const name = factory_product?.design_name;
  if (name != null && String(name).trim() !== '') {
    return String(name).trim();
  }
  return designId;
}

const billingMerchant=ref([]);
const selectedBillingCompany = ref(
  pickOrderItemFirstScalar(
    firstOrderItemSnapshot?.billing_company_id,
    orderBillingFallback,
    propsBillingFallback,
  ) ?? props.order?.company_id,
);
const previousBillingCompany = ref(null);
const billingSelect = ref(null);
const internal_note = ref(
  String(
    pickOrderItemFirstScalar(
      firstOrderItemSnapshot?.internal_note,
      order_ref.value.internal_note,
      '',
    ) ?? '',
  ),
);

const invoiceMemoItemSourceInit =
  firstOrderItemSnapshot?.stripe_invoice_info?.invoice_comment ??
  (firstOrderItemSnapshot as any)?.invoice_memo;
const invoice_memo = ref(String(invoiceMemoItemSourceInit ?? ''));

watch(selectedBillingCompany, (newVal, oldVal) => {
  previousBillingCompany.value = oldVal;
});

function handleBillingMerchantUpdate() {

  if (stripeInvoiceItem.value?.stripe_invoice_id) {
    showToast("Cannot change billing merchant for orders with existing invoices.","error");
    selectedBillingCompany.value = previousBillingCompany.value;
    return;
  }
  Swal.fire({
    title: "Are you sure?",
    text: "Changing the billing merchant will update the order price accordingly. Do you want to continue?.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Yes, continue",
    cancelButtonText: "No"
  }).then((result) => {
    if (result.isConfirmed) {
      billingSelect.value?.$el?.blur();

      const orderItemId = order_items?.[0]?.id;
      if (orderItemId == null) {
        showToast('Order item id is missing; cannot update billing merchant.', 'error');
        selectedBillingCompany.value = previousBillingCompany.value;
        return;
      }

      http
        .post(`update/order/${order_ref.value.id}/billing_merchant`, {
          order_item_id: orderItemId,
          billing_company_id: selectedBillingCompany.value,
          billing_company_name:
            billingMerchant.value.find(
              (c) => c.id === selectedBillingCompany.value,
            )?.company_name || null,
        })
        .then((successResponse) => {
          showToast(successResponse.data.message);
          emit('openOrderPriceModal');
        });
    } else {
      selectedBillingCompany.value = previousBillingCompany.value;
      billingSelect.value?.$el?.blur();

    }
  });
}

const editingInvoiceNo = ref(false);
const invoiceNoInput = ref(''); // Used for both add and edit
const savingInvoice = ref(false);

const saveInvoiceNumber = async () => {
  if (!invoiceNoInput.value || !invoiceNoInput.value.toString().trim()) return;
  const lineItem: any = order_items?.[0];
  if (lineItem?.id == null) {
    showToast('Order item id is missing; cannot save invoice number.', 'error');
    return;
  }
  savingInvoice.value = true;
  try {
    const payload = { stripe_invoice_number: invoiceNoInput.value.toString().trim() };
    const response = await http.put(
      `order/stripe-invoice-no/${lineItem.id}`,
      payload,
    );
    const resData = response.data;
    const returnedInvoiceNo = resData?.result?.stripe_invoice_number ?? invoiceNoInput.value;
    if (lineItem) lineItem.stripe_invoice_number = returnedInvoiceNo;
    editingInvoiceNo.value = false;
    invoiceNoInput.value = '';
    showToast(resData?.message ?? 'Invoice number updated', 'success');
  } catch (err) {
    handleResponseException(err);
  } finally {
    savingInvoice.value = false;
  }
};

const startEditingInvoiceNo = () => {
  editingInvoiceNo.value = true;
  invoiceNoInput.value = stripeInvoiceItem.value?.stripe_invoice_number ?? '';
}




/** Empty factory id on an order item (null / undefined / blank). */
const isOrderItemFactoryIdMissing = (factoryId: unknown): boolean =>
  factoryId == null || (typeof factoryId === 'string' && factoryId.trim() === '');

const factoryIdsEqual = (a: unknown, b: unknown): boolean => {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  const na = Number(a);
  const nb = Number(b);
  if (!Number.isNaN(na) && !Number.isNaN(nb)) return na === nb;
  return String(a) === String(b);
};

/**
 * Block Create Invoice: unsaved line items, or multi-item orders with any missing factory.
 * Toasts on every blocked attempt (caller must not proceed).
 */
const validateCreateInvoiceOrToast = (): boolean => {
  const items = order_items;
  if (!items.length) return true;
  const nullFactoryMessage =
    'Factory must be assigned to all products before invoice creation.';

  const first: any = items[0];
  const refFactoryId = first?.factory_id;

  const unsavedRows = items.filter((i: any) => i.id == null);
  for (const row of unsavedRows) {
    if (!factoryIdsEqual(row.factory_id, refFactoryId)) {
      showToast(
        'You selected a product with a different factory. Please save first, then create the invoice.',
        'error',
      );
      return false;
    }
  }
   // Hard guard: before creating invoice, every order item must have a factory.
   if (items.some((i: any) => isOrderItemFactoryIdMissing(i.factory_id))) {
    showToast(nullFactoryMessage, 'error');
    return false;
  }

  return true;
};

const onCreateInvoiceClick = () => {
  if (can('update-price-breakdown')) {
    void updateOrderPrices(true);
  } else {
    void handleStripeSync();
  }
};

const buildNewOrderItemFromPayload = (payload: any) => {
  const factoryId = payload.factory_id;
  return {
    ...payload,
    id: null,
    status: factoryId ? 'order_approve' : 'submitted_for_factory_review',
    order_id: order_ref.value.id,
    order: {
      id: order_ref.value.id,
      order_no: order_ref.value.order_no,
      order_type_detail: order_ref.value.order_type_detail,
      order_type_symbol: order_ref.value.order_type_symbol,
      order_type_default: order_ref.value.order_type_default,
      company_id: order_ref.value.company_id,
      customer_id: order_ref.value.customer_id,
    },
  };
};

/** Add Item uses the first order line’s factory as the reference (not “any matching factory” row). */
const addFactoryProduct = (payload: any) => {
  const firstItem: any = order_items[0];
  if (!firstItem) return;

  const newProduct = payload.factory_products[0];
  const payloadFactoryId = payload.factory_id;
  const refFactoryId = firstItem.factory_id;

  const bothFactoriesMissing =
    isOrderItemFactoryIdMissing(payloadFactoryId) &&
    isOrderItemFactoryIdMissing(refFactoryId);
  const sameFactory = factoryIdsEqual(payloadFactoryId, refFactoryId);

  if (bothFactoriesMissing || sameFactory) {
    firstItem.factory_products.push(newProduct);
  } else {
    const existingRowSameFactory = order_items.find((item: any, index: number) => {
      if (index === 0) return false;
      return factoryIdsEqual(item?.factory_id, payloadFactoryId);
    });
    if (existingRowSameFactory) {
      existingRowSameFactory.factory_products.push(newProduct);
    } else {
      order_items.push(buildNewOrderItemFromPayload(payload));
    }
  }

  calculateTotalPrice();
};

const paymentStatuses = [
  {
    status: "draft",
    description: "Newly created, not finalized",
    icon: FileText,
    color: "bg-gray-100 text-gray-600"
  },
  {
    status: "open",
    description: "Finalized, awaits payment",
    icon: Clock3,
    color: "bg-blue-100 text-blue-600"
  },
  {
    status: "paid",
    description: "Fully paid",
    icon: BadgeCheck,
    color: "bg-green-100 text-green-600"
  },
  {
    status: "void",
    description: "Voided, no payment required",
    icon: Trash2,
    color: "bg-gray-100 text-gray-600"
  },
  {
    status: "uncollectable",
    description: "Marked as uncollectable",
    icon: CircleX,
    color: "bg-red-100 text-red-600"
  },
  {
    status: "overdue",
    description: "Past due date, unpaid",
    icon: TriangleAlert,
    color: "bg-orange-100 text-orange-500"
  },
  {
    status: "manual",
    description: "Manual invoice",
    icon: SquareM,
    color: "bg-yellow-100 text-orange-500"
  }
]

const statusObjects = computed(() =>
  paymentStatuses.filter(
    s => s.status === stripeInvoiceItem.value?.stripe_invoice_status,
  ),
);

const selectedOrderType = ref(null);

function handleOrderTypeUpdate(order_type: OrderType) {
  selectedOrderType.value = order_type;
}
const errorMessages = reactive<Record<string, string>>({});

//order discount and shipping starts
const discount_shipping_info = ref<any>({
  is_fixed: false,
  fixed_value: 0.0,
  discount: 0.0,
  shipping: 0.0,
  total_price: 0,
  total_quantity: 0,
  order_final_price: 0,
});

const getFactoryProductTotal = (factoryProduct: any): number => {
  const prices = factoryProduct?.prices ?? {};
  let total = 0;

  (prices.sizes ?? []).forEach((size: any) => {
    total += parseFloat(size?.final_net_price_with_quantity ?? 0);
  });
  (prices.addons ?? []).forEach((addon: any) => {
    total += parseFloat(addon?.final_net_price_with_quantity ?? 0);
  });
  (prices.ungrouped_addons ?? []).forEach((addon: any) => {
    total += parseFloat(addon?.final_net_price_with_quantity ?? 0);
  });
  Object.values(prices.grouped_addons ?? {}).forEach((groupedAddon: any) => {
    total += parseFloat(groupedAddon?.final_net_price_with_quantity ?? 0);
  });
  (prices.logo_technologies ?? []).forEach((logoTechnology: any) => {
    total += parseFloat(logoTechnology?.final_net_price_with_quantity ?? 0);
  });
  // Custom `order_item.order_line` is its own row; not included in factory_product_total_price

  return total;
};

/** Sum of factory product roster/addon totals only (excludes `order_line`). */
const orderItemProductsSubtotalOnly = (orderItem: any): number => {
  let s = 0;
  (orderItem?.factory_products ?? []).forEach((fp: any) => {
    s += getFactoryProductTotal(fp);
  });
  return s;
};

/** Recompute each `factory_product_total_price` from sizes/addons (run before `order_line` %). */
const recalculateFactoryProductTotalsForOrderItem = (orderItem: any) => {
  (orderItem?.factory_products ?? []).forEach((factory_product: any) => {
    let total = 0;
    factory_product.prices.sizes.forEach((size: any) => {
      total += parseFloat(size.final_net_price_with_quantity);
    });
    factory_product.prices.addons.forEach((addon: any) => {
      total += parseFloat(addon.final_net_price_with_quantity);
    });
    factory_product.prices.ungrouped_addons.forEach((ungrouped_addon: any) => {
      total += parseFloat(ungrouped_addon.final_net_price_with_quantity);
    });
    Object.values(factory_product.prices.grouped_addons).forEach((grouped_addon: any) => {
      total += parseFloat(grouped_addon.final_net_price_with_quantity);
    });
    factory_product.prices.logo_technologies.forEach((logo_technology: any) => {
      total += parseFloat(logo_technology.final_net_price_with_quantity);
    });
    factory_product.prices.factory_product_total_price = total;
  });
};

/** Order line: at most 2 decimal places (display + stored scalars + contribution to totals). */
const normalizeOrderLineDecimal = (value: string | number): string => {
  if (value === null || value === undefined || value === '') return '0';
  const normalized = String(value).replace(',', '.');
  const num = parseFloat(normalized);
  if (isNaN(num)) return '0';

  const str = num.toFixed(2);
  const [intPart, decPart] = str.split('.');
  if (decPart === '00') return intPart;
  if (decPart[1] === '0') return `${intPart}.${decPart[0]}`;
  return `${intPart}.${decPart}`;
};

const formatOrderLineNumber = normalizeOrderLineDecimal;

/** One custom row amount (2 d.p.); percentage uses current factory-product subtotal for that order item. */
const orderLineLineAmount = (orderItem: any, line: any): number => {
  if (!line || line.type !== 'add_order_item_line') return 0;
  const q = Number(line.quantity) || 0;
  if (!orderLineUsesFixedPricing(line)) {
    const pct = parseFloat(String(line.percentage ?? 0));
    const base = orderItemProductsSubtotalOnly(orderItem);
    const amount = (base * (Number.isNaN(pct) ? 0 : pct)) / 100;
    return parseFloat(normalizeOrderLineDecimal(amount));
  }
  const p = parseFloat(String(line.price ?? 0));
  const amount = q * (Number.isNaN(p) ? 0 : p);
  return parseFloat(normalizeOrderLineDecimal(amount));
};

/** Sum of all `add_order_item_line` rows on the order item; not part of SKU subtotals. */
const orderLineContribution = (orderItem: any): { amount: number; quantity: number } => {
  const lines = normalizeOrderLinesFromApi(orderItem?.order_line);
  let amount = 0;
  let quantity = 0;
  for (const line of lines) {
    amount += orderLineLineAmount(orderItem, line);
    quantity += Number(line.quantity) || 0;
  }
  return { amount: parseFloat(normalizeOrderLineDecimal(amount)), quantity };
};

/** Roster qty + custom order line qty (for price_info.total_quantity). */
const sumOrderItemTotalQuantity = (orderItem: any): number => {
  let q = orderLineContribution(orderItem).quantity;
  (orderItem?.factory_products ?? []).forEach((fp: any) => {
    q += parseInt(String(fp?.prices?.total_quantity ?? 0), 10) || 0;
  });
  return q;
};

/** Ensure first line has a computed price_info when API sends it as null. */
const ensureFirstOrderItemPriceInfo = () => {
  const firstOrderItem: any = order_items?.[0];
  if (!firstOrderItem || firstOrderItem.price_info) return;

  // Same pipeline as calculateTotalPrice: factory totals → products subtotal → order_line → price_info.
  recalculateFactoryProductTotalsForOrderItem(firstOrderItem);
  const productsSubtotal = orderItemProductsSubtotalOnly(firstOrderItem);
  syncOrderLineTotalPrice(firstOrderItem);
  const totalPrice = productsSubtotal + orderLineContribution(firstOrderItem).amount;
  const totalQuantity = sumOrderItemTotalQuantity(firstOrderItem);

  firstOrderItem.price_info = {
    total_price: totalPrice,
    total_quantity: totalQuantity,
    discount: 0,
    is_fixed: false,
    shipping: 0,
    fixed_value: 0,
    order_item_final_price: totalPrice,
    order_final_price: totalPrice,
    total_final_price: totalPrice,
  };
};

const activeOrderItemPriceInfo = computed(() => {
  return order_items?.[0]?.price_info ?? {};
});

const isFixedForUI = computed(() => {
  const v = activeOrderItemPriceInfo.value?.is_fixed;
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v === 1;
  if (typeof v === 'string') return v === '1' || v.toLowerCase() === 'true';
  return false;
});

const discount_amount = computed(() => {
  // Order-level discount never reduces `order_line`: base = factory products only (matches backend).
  const discount = activeOrderItemPriceInfo.value?.discount
    ? parseFloat(activeOrderItemPriceInfo.value?.discount)
    : 0;
  const item0 = order_items?.[0];
  const productsSubtotal = orderItemProductsSubtotalOnly(item0);
  return (productsSubtotal * discount) / 100;
});

const finalPriceForUI = computed(() => {
  const pi = activeOrderItemPriceInfo.value ?? {};
  const item0 = order_items?.[0];
  const productsSubtotal = orderItemProductsSubtotalOnly(item0);
  const orderLineAmt = orderLineContribution(item0).amount;
  const shipping = pi.shipping ? parseFloat(pi.shipping) : 0;

  // Final = discounted products + full order_line (line not discounted) + shipping.
  let afterDiscountProducts = productsSubtotal;
  if (isFixedForUI.value) {
    const fixedValue = pi.fixed_value ? parseFloat(pi.fixed_value) : 0;
    afterDiscountProducts = Math.max(0, productsSubtotal - fixedValue);
  } else {
    const discount = pi.discount ? parseFloat(pi.discount) : 0;
    const discountAmount = (productsSubtotal * discount) / 100;
    afterDiscountProducts = Math.max(0, productsSubtotal - discountAmount);
  }

  return afterDiscountProducts + orderLineAmt + shipping;
});
//order discount and shipping ends

const emit = defineEmits(['close', 'openOrderPriceModal']);
const priceBreakdownModal = ref<boolean>(false);
const shippingAndDiscountModal = ref<boolean>(false);
const addProductModal = ref<boolean>(false);
/** Add-only modal (existing lines are edited inline in the listing). */
const addOrderLineModal = ref(false);
const addOrderLineForm = reactive({
  name: '',
  quantity: 1 as number,
  price: '' as string | number,
  is_fixed: true,
  percentage: '' as string | number,
});

/** Live list of custom lines on the first order item (array or legacy single object). */
const standaloneOrderLines = computed(() => {
  const item0 = order_items?.[0] as any;
  if (!item0?.order_line) return [];
  const r = item0.order_line;
  if (Array.isArray(r)) return r;
  if (r?.type === 'add_order_item_line') return [r];
  return [];
});

const standaloneOrderLineAmountAt = (index: number) => {
  const item0 = order_items?.[0];
  const line = standaloneOrderLines.value[index];
  if (!item0 || !line) return 0;
  return orderLineLineAmount(item0, line);
};

const openAddOrderLineModal = () => {
  if (!order_items[0]) {
    showToast('No order item available.', 'error');
    return;
  }
  addOrderLineForm.name = '';
  addOrderLineForm.quantity = 1;
  addOrderLineForm.price = '';
  addOrderLineForm.is_fixed = true;
  addOrderLineForm.percentage = '';
  addOrderLineModal.value = true;
};

const closeAddOrderLineModal = () => {
  addOrderLineModal.value = false;
};

const appendOrderLineToFirstItem = (newLine: Record<string, any>) => {
  const item0 = order_items[0];
  if (!item0) return;
  if (item0.order_line == null) {
    item0.order_line = [newLine];
  } else if (Array.isArray(item0.order_line)) {
    item0.order_line.push(newLine);
  } else if (typeof item0.order_line === 'object' && (item0.order_line as any).type === 'add_order_item_line') {
    item0.order_line = [item0.order_line, newLine];
  } else {
    item0.order_line = [newLine];
  }
};

const saveAddOrderLine = () => {
  const item0 = order_items[0];
  if (!item0) return;
  const name = String(addOrderLineForm.name ?? '').trim();
  if (!name) {
    showToast('Please enter a name for the order line.', 'error');
    return;
  }
  const qty = Math.max(1, parseInt(String(addOrderLineForm.quantity ?? 1), 10) || 1);
  let newLine: Record<string, any>;
  if (addOrderLineForm.is_fixed) {
    const priceNum = parseFloat(normalizeOrderLineDecimal(addOrderLineForm.price ?? 0));
    if (Number.isNaN(priceNum)) {
      showToast('Please enter a valid unit price.', 'error');
      return;
    }
    newLine = {
      type: 'add_order_item_line',
      name,
      quantity: qty,
      is_fixed: true,
      price: priceNum,
      percentage: undefined,
    };
  } else {
    const pct = parseFloat(normalizeOrderLineDecimal(addOrderLineForm.percentage ?? 0));
    if (Number.isNaN(pct)) {
      showToast('Please enter a valid percentage.', 'error');
      return;
    }
    newLine = {
      type: 'add_order_item_line',
      name,
      quantity: qty,
      is_fixed: false,
      price: 0,
      percentage: pct,
    };
  }
  appendOrderLineToFirstItem(newLine);
  addOrderLineModal.value = false;
  calculateTotalPrice();
};

const removeOrderLineAt = (index: number) => {
  const item0 = order_items[0];
  if (!item0?.order_line) return;
  if (Array.isArray(item0.order_line)) {
    item0.order_line.splice(index, 1);
    if (item0.order_line.length === 0) item0.order_line = null;
  } else if (index === 0 && (item0.order_line as any).type === 'add_order_item_line') {
    item0.order_line = null;
  }
  calculateTotalPrice();
};

const setOrderLinePricingMode = (index: number, mode: 'fixed' | 'percent') => {
  const line = standaloneOrderLines.value[index];
  if (!line) return;
  if (mode === 'fixed') {
    line.is_fixed = true;
    line.percentage = undefined;
    if (line.price == null || line.price === '') line.price = 0;
  } else {
    line.is_fixed = false;
    line.price = 0;
    if (line.percentage == null || line.percentage === '') line.percentage = 0;
  }
  calculateTotalPrice();
};

const onOrderLineQuantityChange = (index: number) => {
  const line = standaloneOrderLines.value[index];
  if (!line) return;
  line.quantity = Math.max(1, parseInt(String(line.quantity ?? 1), 10) || 1);
  calculateTotalPrice();
};

const onStandaloneAddOrderLinePriceChange = (e: Event, index: number) => {
  const el = e.target as HTMLInputElement;
  const line = standaloneOrderLines.value[index];
  if (!line || line.type !== 'add_order_item_line' || !orderLineUsesFixedPricing(line)) return;
  const num = normalizeOrderLineDecimal(el.value);
  el.value = num;
  line.price = parseFloat(num);
  calculateTotalPrice();
};

const onStandaloneAddOrderLinePercentageChange = (e: Event, index: number) => {
  const el = e.target as HTMLInputElement;
  const line = standaloneOrderLines.value[index];
  if (!line || line.type !== 'add_order_item_line' || orderLineUsesFixedPricing(line)) return;
  const num = normalizeOrderLineDecimal(el.value);
  el.value = num;
  line.percentage = parseFloat(num);
  calculateTotalPrice();
};

/**
 * Prefer `order_items.order_line` (DB) as JSON array. Normalize legacy single object → array.
 * Move embedded `factory_product.prices.add_order_item_line` / `add_order_item_lines[]` onto the item.
 */
const migrateLegacyOrderLineFromPrices = () => {
  (order_ref.value.items ?? []).forEach((oi: any) => {
    const rawCol = oi?.order_line;
    if (rawCol && !Array.isArray(rawCol) && rawCol.type === 'add_order_item_line') {
      oi.order_line = [rawCol];
    }
    const columnLines = normalizeOrderLinesFromApi(oi?.order_line);
    if (columnLines.length > 0) {
      oi.order_line = columnLines;
      (oi.factory_products ?? []).forEach((fp: any) => {
        const p = fp?.prices;
        if (!p) return;
        delete p.add_order_item_line;
        delete p.add_order_item_lines;
      });
      return;
    }
    for (const fp of oi.factory_products ?? []) {
      const p = fp?.prices;
      if (!p) continue;
      if (Array.isArray(p.add_order_item_lines) && p.add_order_item_lines.length) {
        const valid = p.add_order_item_lines.filter((x: any) => x?.type === 'add_order_item_line');
        delete p.add_order_item_lines;
        if (valid.length) {
          oi.order_line = valid.map((em: any) => ({
            type: 'add_order_item_line',
            name: em.name ?? 'Order line',
            quantity: Math.max(1, Number(em.quantity) || 1),
            is_fixed: orderLineUsesFixedPricing(em),
            price: orderLineUsesFixedPricing(em)
              ? parseFloat(normalizeOrderLineDecimal(em.price ?? 0))
              : 0,
            percentage: !orderLineUsesFixedPricing(em)
              ? parseFloat(normalizeOrderLineDecimal(em.percentage ?? 0))
              : undefined,
          }));
          delete p.add_order_item_line;
          break;
        }
      }
      const embedded = p.add_order_item_line;
      if (embedded?.type === 'add_order_item_line') {
        oi.order_line = [
          {
            type: 'add_order_item_line',
            name: embedded.name,
            quantity: embedded.quantity,
            price: parseFloat(normalizeOrderLineDecimal(embedded.price ?? 0)),
            is_fixed: orderLineUsesFixedPricing(embedded),
            percentage: !orderLineUsesFixedPricing(embedded)
              ? parseFloat(normalizeOrderLineDecimal(embedded.percentage ?? 0))
              : undefined,
          },
        ];
        delete p.add_order_item_line;
        break;
      }
    }
  });
};

let company_products = ref<Product[]>([]);
const getMerchantProducts = async (company_id: number, factory_id: number) => {
  http
    .get<ApiResponse2<Result<Product[]>>>(`merchant_products?company_id=${company_id}&factory_id=${factory_id}`)
    .then((successResponse: any) => {
      company_products.value = successResponse.data.result.products;

    });
};
const mainStore = useMainStore();
const { getCustomerLockerRoom, getFactories, merchantUsers } = mainStore;
const { lockerRoom, factories } = storeToRefs(mainStore);
onMounted(() => {
  // Order payload is already loaded with `?order_item_id=` by the parent (`openOrderPriceModal` or
  // `priceListData` in DashboardActions). Avoid a duplicate `customer-orders` GET here.

  priceBreakdownModal.value = true;
  migrateLegacyOrderLineFromPrices();
  ensureFirstOrderItemPriceInfo();
  const activePriceInfo = JSON.parse(
    JSON.stringify(order_items?.[0]?.price_info ?? order_ref.value?.price_info ?? {})
  );
  discount_shipping_info.value = {
    ...discount_shipping_info.value,
    ...activePriceInfo,
  };
  // Backend sometimes stores booleans as 0/1 or strings; normalize for correct v-if rendering.
  if (order_items?.[0]?.price_info?.is_fixed !== undefined) {
    order_items[0].price_info.is_fixed = !!order_items[0].price_info.is_fixed;
  }
  // Re-sum line items (including order_item.order_line) so totals match the tree after legacy migration.
  if (order_items?.length) {
    calculateTotalPrice();
  }
  const item0: any = order_items?.[0];
  selectedOrderType.value = {
    label:
      String(pickOrderItemFirstScalar(item0?.order_type_detail, order_ref.value.order_type_detail, '') ?? ''),
    symbol:
      String(pickOrderItemFirstScalar(item0?.order_type_symbol, order_ref.value.order_type_symbol, '') ?? ''),
    default:
      !!(pickOrderItemFirstScalar(
        item0?.order_type_default,
        order_ref.value.order_type_default,
        false,
      ) ?? false),
  };
  getMerchantProducts(order_ref.value.company_id,firstOrderItemSnapshot.factory_id);
  getCustomerLockerRoom(order_ref.value.company_id, order_ref.value.customer_id);
  getFactories()
  billingMerchant.value = merchantUsers;
});

const closeEvent = async () => {
  emit('close', false);
  priceBreakdownModal.value = false;
};

const closeShippingAndDiscountModal = () => {
  shippingAndDiscountModal.value = false;
};

const storage_url = import.meta.env.VITE_APP_FILE_URL;

const handlePriceUpdate = (item: any, quantity: any) => {
  item.final_net_price = formatNumber(item.final_net_price);
  const total = quantity * parseFloat(item.final_net_price);
  item.final_net_price_with_quantity = formatNumber(total);
  calculateTotalPrice();
};

const getFilePreview = (factory_product: any) => {
  if (factory_product.front_image_file instanceof File) {
    return factory_product.front_image_file
      ? URL.createObjectURL(factory_product.front_image_file)
      : '';
  } else {
    return `${storage_url}/${factory_product.front_image}`;
  }
};

const normalizeDecimalInput = (value: string | number): string => {
  if (value === null || value === undefined || value === '') return '0';
  // Replace comma with dot, then strip any non-numeric chars except leading minus and dot
  const normalized = String(value).replace(',', '.');
  const num = parseFloat(normalized);
  if (isNaN(num)) return '0';

  let str = num.toFixed(3);
  const [intPart, decPart] = str.split('.');
  if (decPart === '000') return intPart;
  if (decPart[2] === '0') return `${intPart}.${decPart.slice(0, 2)}`;
  return `${intPart}.${decPart}`;
};

const formatNumber = normalizeDecimalInput;

/**
 * After factory product totals exist, refresh each row’s `total_price` (2 d.p.).
 * Percentage rows use the current products subtotal (add/remove SKUs changes this).
 */
const syncOrderLineTotalPrice = (orderItem: any) => {
  const raw = orderItem?.order_line;
  if (raw == null) return;
  let lines: any[];
  if (Array.isArray(raw)) {
    lines = raw.filter((x: any) => x?.type === 'add_order_item_line');
  } else if (typeof raw === 'object' && raw.type === 'add_order_item_line') {
    lines = [raw];
    orderItem.order_line = lines;
  } else {
    return;
  }
  for (const line of lines) {
    line.is_fixed = orderLineUsesFixedPricing(line);
    if (line.is_fixed && line.price != null && line.price !== '') {
      line.price = parseFloat(normalizeOrderLineDecimal(line.price));
    } else if (!line.is_fixed && line.percentage != null && line.percentage !== '') {
      line.percentage = parseFloat(normalizeOrderLineDecimal(line.percentage));
    }
    const amt = orderLineLineAmount(orderItem, line);
    line.total_price = parseFloat(normalizeOrderLineDecimal(amt));
  }
};

// const formatNumber = (value) => {
//   const num = parseFloat(value) || 0;

//   let str = num.toFixed(3); // always start with 3 decimals
//   const [intPart, decPart] = str.split('.');

//   // all decimals are zero → return integer
//   if (decPart === '000') {
//     return intPart;
//   }

//   // third decimal is zero → keep first two
//   if (decPart[2] === '0') {
//     return `${intPart}.${decPart.slice(0, 2)}`;
//   }

//   // all three decimals are meaningful → keep all
//   return `${intPart}.${decPart}`;
// };

const calculateTotalPrice = () => {
  let orderTotalPrice = 0;
  let orderTotalQuantity = 0;
  order_items.forEach(orderItem => {
    // 1) Refresh every factory row (add/remove product or roster change).
    recalculateFactoryProductTotalsForOrderItem(orderItem);
    // 2) Products subtotal = base for percentage order_line.
    const productsSubtotal = orderItemProductsSubtotalOnly(orderItem);
    // 3) order_line.total_price / is_fixed from current products (%, or fixed qty×price).
    syncOrderLineTotalPrice(orderItem);
    // 4) price_info.total_price = products + line (then shipping/discount recomputes finals).
    const orderLineAmt = orderLineContribution(orderItem).amount;
    const order_item_total_price = productsSubtotal + orderLineAmt;

    if (!orderItem.price_info) {
      orderItem.price_info = {
        total_price: 0,
        total_quantity: 0,
        discount: 0,
        is_fixed: false,
        shipping: 0,
        fixed_value: 0,
        order_item_final_price: 0,
      };
    }

    orderItem.price_info.total_price = order_item_total_price;
    orderTotalPrice += order_item_total_price;
    const itemQtySum = sumOrderItemTotalQuantity(orderItem);
    orderItem.price_info.total_quantity = itemQtySum;
    orderTotalQuantity += itemQtySum;
    //order_item_final_price property is no more in use in future if we apply dicount on order item then it can be used. For now it is moved to order price info as order_final _price
    //orderItem.price_info.order_item_final_price = order_item_total_price;
  })
  if (!order_ref.value.price_info) {
    order_ref.value.price_info = {
      total_price: 0,
      total_final_price: 0,
      total_quantity: 0,
    };
  }

  order_ref.value.price_info.total_price = orderTotalPrice;
  order_ref.value.price_info.total_final_price = orderTotalPrice;
  order_ref.value.price_info.total_quantity = orderTotalQuantity;
  calculateShippingAndDiscount();
};

const calculateShippingAndDiscount = () => {
  const activeOrderItem = order_items?.[0];
  if (!activeOrderItem) {
    closeShippingAndDiscountModal();
    return;
  }

  if (!activeOrderItem.price_info) {
    activeOrderItem.price_info = {
      total_price: 0,
      total_quantity: 0,
      order_item_final_price: 0,
    };
  }

  const order_item_total_price = parseFloat(activeOrderItem.price_info?.total_price ?? 0);
  const productsSubtotal = orderItemProductsSubtotalOnly(activeOrderItem);
  const orderLineAmt = orderLineContribution(activeOrderItem).amount;

  // Normalize inputs coming from the UI.
  const isFixed = !!discount_shipping_info.value.is_fixed;
  discount_shipping_info.value.is_fixed = isFixed;
  discount_shipping_info.value.fixed_value = discount_shipping_info.value.fixed_value
    ? parseFloat(discount_shipping_info.value.fixed_value)
    : 0;
  discount_shipping_info.value.discount = discount_shipping_info.value.discount
    ? parseFloat(discount_shipping_info.value.discount)
    : 0;
  discount_shipping_info.value.shipping = discount_shipping_info.value.shipping
    ? parseFloat(discount_shipping_info.value.shipping)
    : 0;

  // Discount/fixed off applies only to factory products. `order_line` is added in full (never discounted). Shipping last.
  let afterDiscountProducts = productsSubtotal;
  if (isFixed) {
    const fv = discount_shipping_info.value.fixed_value ? discount_shipping_info.value.fixed_value : 0;
    afterDiscountProducts = Math.max(0, productsSubtotal - fv);
  } else {
    const discount = discount_shipping_info.value.discount ? discount_shipping_info.value.discount : 0;
    const discount_amount = (productsSubtotal * discount) / 100;
    afterDiscountProducts = Math.max(0, productsSubtotal - discount_amount);
  }

  const shippingVal = discount_shipping_info.value.shipping ? discount_shipping_info.value.shipping : 0;
  const itemFinalPrice = afterDiscountProducts + orderLineAmt + shippingVal;

  // Round values so the payload matches the modal display (avoids float precision drift).
  const roundedFixedValue = parseFloat(formatNumber(discount_shipping_info.value.fixed_value));
  const roundedDiscount = parseFloat(formatNumber(discount_shipping_info.value.discount));
  const roundedShipping = parseFloat(formatNumber(discount_shipping_info.value.shipping));
  const roundedFinalPrice = parseFloat(formatNumber(itemFinalPrice));

  // Update order_items.price_info (these are saved with each order item).
  activeOrderItem.price_info.is_fixed = isFixed;
  activeOrderItem.price_info.fixed_value = roundedFixedValue;
  activeOrderItem.price_info.discount = roundedDiscount;
  activeOrderItem.price_info.shipping = roundedShipping;
  activeOrderItem.price_info.order_item_final_price = roundedFinalPrice;
  // Keep legacy keys in sync as well (some parts of the UI/backend expect them).
  // These are part of the save payload; keep them as numbers (avoid JSON "251.034" strings).
  activeOrderItem.price_info.order_final_price = roundedFinalPrice;
  activeOrderItem.price_info.total_final_price = roundedFinalPrice;

  // Keep the order-level price info in sync (UI + orders table payload).
  if (!order_ref.value.price_info) {
    order_ref.value.price_info = {
      total_price: 0,
      total_final_price: 0,
      total_quantity: 0,
    };
  }

  order_ref.value.price_info.total_price = order_item_total_price;
  const qtyWithOrderLine = sumOrderItemTotalQuantity(activeOrderItem);
  activeOrderItem.price_info.total_quantity = qtyWithOrderLine;
  order_ref.value.price_info.total_quantity = qtyWithOrderLine;
  order_ref.value.price_info.total_final_price = roundedFinalPrice;
  order_ref.value.price_info.is_fixed = isFixed;
  order_ref.value.price_info.fixed_value = roundedFixedValue;
  order_ref.value.price_info.discount = roundedDiscount;
  order_ref.value.price_info.shipping = roundedShipping;
  // Keep payload values numeric (avoid JSON string serialization).
  order_ref.value.price_info.order_final_price = roundedFinalPrice;

  closeShippingAndDiscountModal();
};
const uploadFactoryProductsFiles = async (order_items: any[]) => {
  const base_path = `comapny_${order_ref.value.company_id}/${order_ref.value.company_id}/order/${order_ref.value.customer_id}/${order_ref.value.id}`;
  const allPromises: Promise<any>[] = [];

  for (const [orderItemIndex, orderItem] of order_items.entries()) {
    const file_base_path = orderItem.factory_id ? `${base_path}/${orderItem.factory_id}` : `${base_path}/shareable`;

    for (const [factoryProductIndex, factoryProduct] of orderItem.factory_products.entries()) {
      const formData = new FormData();

      if (factoryProduct.front_image_file instanceof File) formData.append('front_image', factoryProduct.front_image_file);
      if (factoryProduct.back_image_file instanceof File) formData.append('back_image', factoryProduct.back_image_file);

      if (Array.isArray(factoryProduct.assets)) {
        factoryProduct.assets.forEach(asset => {
          if (asset instanceof File) formData.append('assets[]', asset);
        });
      }

      const hasFiles = formData.has('front_image') || formData.has('back_image') || formData.has('assets[]');
      if (!hasFiles) continue;

      formData.append('base_path', file_base_path);

      allPromises.push(
        http.post('upload/price/modal/files', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',}})
          .then(res => ({ orderItemIndex, factoryProductIndex, data: res.data.result }))

      );
    }
  }

  const factoryProductsFilesData = await Promise.allSettled(allPromises);
  const filesUploadfailedResponses = factoryProductsFilesData.filter((r): r is PromiseRejectedResult => r.status === "rejected");
  const filesUploadSuccessResponses = factoryProductsFilesData.filter((r): r is PromiseFulfilledResult<any> => r.status === "fulfilled");
    if(filesUploadfailedResponses.length > 0) {
      filesUploadfailedResponses.forEach(filesUploadfailedResponse => {
        handleResponseException((filesUploadfailedResponse.reason as AxiosError));
      })
    }
  filesUploadSuccessResponses.forEach((factoryProductsFilesDatum: any) => {
    const status = factoryProductsFilesDatum.status;
    if(status === "fulfilled") {
      const {
      value: {
        orderItemIndex,
        factoryProductIndex,
        data: {
          front_image,
          back_image,
          assets
        }
      }
    } = factoryProductsFilesDatum;
    order_items[orderItemIndex]['factory_products'][factoryProductIndex].front_image = front_image
    order_items[orderItemIndex]['factory_products'][factoryProductIndex].back_image = back_image
    order_items[orderItemIndex]['factory_products'][factoryProductIndex].assets = assets
    delete order_items[orderItemIndex]['factory_products'][factoryProductIndex].front_image_file;
    delete order_items[orderItemIndex]['factory_products'][factoryProductIndex].back_image_file;
    }
  });
};

const uploadFactoryProductsFiles1 = async (order_items: any[]) => {
  let base_path = `comapny_${order_ref.value.company_id}/${order_ref.value.company_id}/order/${order_ref.value.customer_id}/${order_ref.value.id}`;
  for (const order_item of order_items) {
    let file_path = '';
    if (order_item.factory_id) {
      file_path = `${base_path}/${order_item.factory_id}`;
    } else {
      file_path = `${base_path}/shareable`;
    }
    const factory_product_files = order_item.factory_products
      .map(async (factory_product: any, factory_product_index: number) => {
        const factory_product_form_data = new FormData();

        if (factory_product.front_image_file instanceof File) {
          factory_product_form_data.append('front_image', factory_product.front_image_file);
        }
        if (factory_product.back_image_file instanceof File) {
          factory_product_form_data.append('back_image', factory_product.back_image_file);
        }
        if (Array.isArray(factory_product.assets)) {
          factory_product.assets.forEach((asset, asset_index) => {
            if (asset instanceof File) {
              factory_product_form_data.append(`assets[${asset_index}]`, asset);
            }
          });
        }

        if (
          factory_product_form_data.has('front_image') ||
          factory_product_form_data.has('back_image') ||
          factory_product_form_data.has('assets[0]')
        ) {
          factory_product_form_data.append('base_path', base_path);

          const factory_product_response = await http.post(
            'upload/price/modal/files',
            factory_product_form_data,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );

          return {
            status: factory_product_response.status,
            factory_product_index,
            data: factory_product_response.data.result,
            order_item,
          };
        } else {
          return null;
        }
      })
      .filter(Boolean);

    const results = await Promise.allSettled(factory_product_files);

    results.forEach((result: any) => {
      if (result.status === 'fulfilled' && result.value) {
        const { factory_product_index, data } = result.value;

        order_item.factory_products[factory_product_index].front_image = data.front_image;
        order_item.factory_products[factory_product_index].back_image = data.back_image;
        order_item.factory_products[factory_product_index].assets = data.assets;

        delete order_item.factory_products[factory_product_index].front_image_file;
        delete order_item.factory_products[factory_product_index].back_image_file;
      }
    });
  }
};

/** Keep first order item aligned with modal fields (source of truth for display & save). */
const syncFirstOrderItemFromForm = () => {
  const item: any = order_items?.[0];
  if (!item) return;
  const sel = selectedOrderType.value as OrderType | null;
  if (sel && typeof sel === 'object') {
    if (sel.label != null) item.order_type_detail = sel.label;
    if (sel.symbol != null) item.order_type_symbol = sel.symbol;
    if (sel.default != null) item.order_type_default = sel.default;
  }
  item.internal_note = internal_note.value;
  item.invoice_memo = invoice_memo.value;
  if (!item.stripe_invoice_info) item.stripe_invoice_info = {};
  item.stripe_invoice_info.invoice_comment = invoice_memo.value;
  item.billing_company_id = selectedBillingCompany.value;
};

const updateOrderPrices = async (createInvoice: boolean = false) => {
  if (createInvoice && !validateCreateInvoiceOrToast()) {
    return;
  }
  loading.value = true;
  ensureFirstOrderItemPriceInfo();
  // Recompute totals from factory_products + order_line, then apply shipping/discount.
  calculateTotalPrice();
  syncFirstOrderItemFromForm();
  //create order items chunks
  const order_items_chunks = chunk(order_items, 10);
  const allPromises: Promise<any>[] = [];
    //loop through order items chunks
    for (const orderItemsChunk of order_items_chunks) {
      //upload order items chunk all files and add order items chunk all factory products front, backimage and assets path
      await uploadFactoryProductsFiles(orderItemsChunk);
      const orderItemsData = [];
      for (const  orderItem of orderItemsChunk) {

        const typedOrderItem: any = orderItem
        orderItemsData.push({
          id: typedOrderItem.id,
          factory_id: typedOrderItem.factory_id,
          factory_name: typedOrderItem.factory_name,
          price_info: typedOrderItem.price_info,
          factory_products: typedOrderItem.factory_products,
          order_line: (() => {
            const lines = normalizeOrderLinesFromApi(typedOrderItem.order_line);
            return lines.length ? lines : null;
          })(),
          status: typedOrderItem.status,
          invoice_memo: typedOrderItem.invoice_memo,
          internal_note: typedOrderItem.internal_note,
          billing_company_id: typedOrderItem.billing_company_id,
          order_type_detail: typedOrderItem.order_type_detail,
          order_type_symbol: typedOrderItem.order_type_symbol,
          order_type_default: typedOrderItem.order_type_default,
        })
      }
      allPromises.push(
        http.post(`update/order/${order_ref.value.id}/order_items/price`, {order_items: orderItemsData})
      );
  }
  Promise.allSettled(allPromises).then(results => {
    const failed = results.filter((r): r is PromiseRejectedResult => r.status === "rejected");
    failed.forEach(failedItem => {
      handleResponseException((failedItem.reason as AxiosError));
    });

    const fulfilled = results.find(
      (r): r is PromiseFulfilledResult<{ data?: { message?: string } }> =>
        r.status === "fulfilled",
    );
    if (!fulfilled) {
      loading.value = false;
      return;
    }

    const message = fulfilled.value?.data?.message ?? "Saved successfully";

    if (createInvoice) {
      handleStripeSync({ skipCreateInvoiceValidation: true });
    } else {
      loading.value = false;
      emit("close", true);
      priceBreakdownModal.value = false;
      showToast(message);
    }
  });
};
const handleStripeSync = async (options?: { skipCreateInvoiceValidation?: boolean }) => {
  if (!options?.skipCreateInvoiceValidation && !validateCreateInvoiceOrToast()) {
    return;
  }
  stripeLoading.value = true;
  const response = http
    .post(`customer-orders/${order_ref.value.id}/sync-to-stripe`, {'order_item_id': order_items?.[0]?.id, 'invoice_memo': invoice_memo.value})
    .then((response: any) => {
      const responseData = response.data;
      const {
        message,
        result,
      } = responseData;
      void updateInvoiceState(result?.invoice);
      emit('close', true);
      priceBreakdownModal.value = false;
      stripeLoading.value = false;
      loading.value = false;
      showToast(message);
    })
    .catch((error) => {
      console.log('error', error);
      stripeLoading.value = false;
      loading.value = false;
      handleResponseException(error);
    });
};

const updateInvoiceState = async (invoice) => {
  const item: any = order_items?.[0];
  if (invoice && item) {
    item.stripe_invoice_id = invoice.invoiceId;
    item.stripe_invoice_info = {
      ...item.stripe_invoice_info,
      hosted_invoice_url: invoice.hosted_invoice_url,
      invoice_pdf: invoice.invoice_pdf,
    };
  }
};

const removeFactoryProduct = (orderItemIndex, factoryProductIndex) => {
  order_items[orderItemIndex]['factory_products'].splice(factoryProductIndex, 1)
  calculateTotalPrice();

}

const copyRoster = (copiedRosterData: any) => {
  if(copiedRoster.value.isAnyRosterCopied) {
    showConfirm(
      'Overwrite existing copied roster?',
      'You already have a roster copied. Do you want to copy this product’s roster instead?',
    ).then((result) => {
      if (result) {
        setCopiedRoster(copiedRosterData.product_id, copiedRosterData.rosterData);
        showToast('Roster copied successfully', 'success');
      }
    });
  } else {
    setCopiedRoster(copiedRosterData.product_id, copiedRosterData.rosterData);
    showToast('Roster copied successfully', 'success');
  }
};

const setCopiedRoster = (factory_product_index: number, roster: any[]) => {
  copiedRoster.value.isAnyRosterCopied = true;
  copiedRoster.value.copiedFromProductIndex = factory_product_index;
  copiedRoster.value.rosterData = roster;
};
</script>

<style lang="scss">
.products-table {
  th,
  td {
    border: 1px solid #e5e7eb;
    padding: 5px;
  }

  thead {
    position: sticky;
    top: 0;
    z-index: 10;
    background-color: white;
  }

  th {
    position: sticky;
    top: 0;
    background-color: white;
    z-index: 10;
  }
}

.modal-body {
  padding-top: 0 !important;
}
</style>
